/**
 * net/apiFetch.ts — o transporte das chamadas de GESTOR (usuário logado).
 *
 * O player NÃO usa isto: ele tem credencial própria (`net/credentials.ts`) e o
 * `HttpGateway`. Aqui vive só o que é `Authorization: Bearer <access do usuário>`.
 *
 * POR QUE UM ARQUIVO e não `fetch` espalhado: o manager precisa de três coisas
 * que sãoeasy de errar em cada call site — (1) a base URL tem UMA fonte
 * (`net/config.ts`, igual ao gateway); (2) o erro do backend é sempre o envelope
 * `{error:{code,message}}` e a decisão é pelo `code`, nunca pela `message`;
 * (3) um `401 TOKEN_EXPIRED` tem que virar UM refresh, não uma tela de erro.
 *
 * ponytail: o `fetch` é injetável (mesma técnica do `HttpGateway`/`PairingClient`)
 * para os testes exercitarem 401/403/429 sem subir backend. Sem interceptor, sem
 * classe de request, sem fila — o `refresh` é single-flight no `session.ts`.
 */
import { DEFAULT_API_BASE_URL } from './config';
import { codeFromErrorEnvelope } from './errors';

/** Erro do backend com o `code` preservado — a UI decide pelo `code`. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly field: string | undefined;

  constructor(
    code: string,
    message: string,
    status: number,
    options: { details?: Record<string, unknown>; field?: string } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    if (options.details !== undefined) this.details = options.details;
    if (options.field !== undefined) this.field = options.field;
  }
}

export interface ApiFetchOptions {
  baseUrl?: string;
  /** Access token do usuário, em memória. `null` = chamada anônima. */
  accessToken?: () => string | null;
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>;
}

export interface ApiRequest {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  /** Rotas anônimas (login, signup, link-request) não mandam Authorization. */
  anonymous?: boolean;
  headers?: Record<string, string>;
}

/** Resposta 204/200 sem envelope: devolve `null` sem tentar parsear JSON. */
export async function apiFetch<T>(options: ApiFetchOptions, request: ApiRequest): Promise<T> {
  const baseUrl = (options.baseUrl ?? DEFAULT_API_BASE_URL).replace(/\/+$/, '');
  const doFetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const headers: Record<string, string> = { ...(request.headers ?? {}) };
  if (request.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!request.anonymous) {
    const token = options.accessToken?.();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await doFetch(`${baseUrl}${request.path.startsWith('/') ? request.path : `/${request.path}`}`, {
      method: request.method,
      headers,
      ...(request.body !== undefined ? { body: JSON.stringify(request.body) } : {}),
    });
  } catch (error) {
    // Sem rede/DNS/timeout/CORS: o manager mostra "sem conexão", nunca erro técnico.
    throw new ApiError('NETWORK', String(error), 0);
  }

  if (response.status === 204) {
    return null as T;
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Sem JSON (proxy, 500 com HTML): decide pelo status, nunca por raspagem.
    body = null;
  }

  if (!response.ok) {
    const envelope = (body as { error?: { code?: string; message?: string; details?: Record<string, unknown>; field?: string } } | null)?.error;
    const fallback = codeFromErrorEnvelope(envelope?.code, response.status);
    throw new ApiError(
      envelope?.code ?? fallback,
      envelope?.message ?? `HTTP ${response.status}`,
      response.status,
      { ...(envelope?.details !== undefined ? { details: envelope.details } : {}), ...(envelope?.field !== undefined ? { field: envelope.field } : {}) },
    );
  }

  return body as T;
}
